import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { IToastEntity } from "userful-chronos-common-store/dist/toasts";
import { faExclamationCircle, faInfoCircle, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface AppToastProps {
    toast: IToastEntity;
    onRemove: (id: string) => void;
}

const ANIMATION_DURATION_MS = 250;

export default function AppToast({ toast, onRemove }: AppToastProps) {
    const [phase, setPhase] = useState<"enter" | "visible" | "leaving">("enter");

    useEffect(() => {
        const frame = requestAnimationFrame(() => setPhase("visible"));
        return () => cancelAnimationFrame(frame);
    }, [toast.id]);

    useEffect(() => {
        const timeout = window.setTimeout(() => setPhase("leaving"), toast.durationMs);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [toast.durationMs]);

    useEffect(() => {
        if (phase !== "leaving") {
            return;
        }

        const timeout = window.setTimeout(() => onRemove(toast.id), ANIMATION_DURATION_MS);
        return () => window.clearTimeout(timeout);
    }, [phase, onRemove, toast.id]);

    const variantClass = useMemo(() => `userful-${toast.variant}-toast`, [toast.variant]);

    const phaseClass = useMemo(() => {
        switch (phase) {
            case "visible":
                return "userful-toast--visible";
            case "leaving":
                return "userful-toast--leaving";
            default:
                return "userful-toast--enter";
        }
    }, [phase]);

    let icon = faInfoCircle;
    if (toast.variant === "warning") icon = faWarning;
    else if (toast.variant === "error") icon = faExclamationCircle;

    return (
        <div className={`userful-toast ${variantClass} ${phaseClass}`} role="alert">
            <div className="userful-toast-icon-wrapper">
                <FontAwesomeIcon icon={icon} />
            </div>
            <div className="userful-toast-text">
                {toast.title ? <h1>{toast.title}</h1> : null}
                {toast.description ? <p>{toast.description}</p> : null}
            </div>
        </div>
    );
}
